import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, Globe, HelpCircle, LogOut,
  ChevronDown, X, ChevronUp, BookOpen, Loader2,
  Save, Eye, EyeOff, Pencil, Link2, AlertTriangle, CheckCircle2, RefreshCw
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ── tiny hook: close on outside click ── */
function useOutsideClick(ref, cb) {
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) cb();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, cb]);
}

/* ── Help panel ── */
function HelpPanel({ onClose }) {
  const [topics, setTopics]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/help/topics")
      .then(({ data }) => setTopics(data.topics || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="absolute top-full start-0 mt-2 w-80 z-50
      bg-[var(--bg-card)] dark:bg-[#111c30] border border-[var(--border-color)] dark:border-white/10
      rounded-2xl shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)] dark:border-white/[0.07]">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#004EEB]" />
          <p className="text-sm font-bold text-[var(--text-primary)] dark:text-white">المساعدة</p>
        </div>
        <button onClick={onClose}
          className="p-1 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-secondary)] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/10 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
          </div>
        ) : topics.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">لا توجد مواضيع مساعدة بعد</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {topics.map((t) => (
              <div key={t.id} className="rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpanded(e => ({ ...e, [t.id]: !e[t.id] }))}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5
                    text-right text-sm font-semibold text-[var(--text-secondary)] dark:text-white/80
                    hover:bg-[var(--bg-card-hover)] dark:hover:bg-[var(--bg-card)]/[0.05] transition-colors rounded-xl"
                >
                  <span>{t.title}</span>
                  {expanded[t.id]
                    ? <ChevronUp className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />
                    : <ChevronDown className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />
                  }
                </button>
                {expanded[t.id] && (
                  <div className="px-3 pb-3 text-xs text-[var(--text-secondary)] dark:text-white/50 leading-relaxed whitespace-pre-wrap">
                    {t.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-[var(--border-color)] dark:border-white/[0.07] bg-[var(--bg-card-hover)] dark:bg-[var(--bg-card)]/[0.02]">
        <p className="text-[11px] text-[var(--text-muted)] text-center">
          هل تحتاج مساعدة إضافية؟{" "}
          <a href="mailto:support@getjisr.com"
            className="text-[#004EEB] font-semibold hover:underline">
            تواصل معنا
          </a>
        </p>
      </div>
    </div>
  );
}

/* ── User panel (profile + logout) ── */
function UserPanel({ onClose }) {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [editMode, setEditMode]   = useState(false);
  const [form, setForm]           = useState({
    name: user?.name || "",
    email: user?.email || "",
    current_password: "",
    new_password: "",
  });
  const [saving, setSaving]       = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  // Sync form when user changes
  useEffect(() => {
    if (user) setForm(f => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate("/login", { replace: true });
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      toast.success("تم تحديث بياناتك");
      setForm(f => ({ ...f, current_password: "", new_password: "" }));
      await refreshMe();
      setEditMode(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "خطأ في الحفظ");
    } finally { setSaving(false); }
  };

  const initials = (user?.name || "م").slice(0, 1);

  return (
    <div className="absolute top-full end-0 mt-2 z-50
      bg-[var(--bg-card)] dark:bg-[#111c30] border border-[var(--border-color)] dark:border-white/10
      rounded-2xl shadow-xl overflow-hidden"
      style={{ width: editMode ? "360px" : "256px" }}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-[var(--border-color)] dark:border-white/[0.07]">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#004EEB] to-[#0040cc]
          grid place-items-center text-white text-sm font-bold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[var(--text-primary)] dark:text-white truncate">{user?.name || "المستخدم"}</p>
          <p className="text-[11px] text-[var(--text-muted)] truncate">{user?.email}</p>
        </div>
        <button
          onClick={() => setEditMode(v => !v)}
          data-testid="topbar-edit-profile-btn"
          title={editMode ? "إغلاق التعديل" : "تعديل الملف الشخصي"}
          className={`p-1.5 rounded-lg transition-colors shrink-0 ${
            editMode
              ? "bg-[#004EEB]/10 text-[#004EEB]"
              : "text-[var(--text-muted)] hover:text-[var(--text-secondary)] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/10"
          }`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ── Edit Profile Form ── */}
      {editMode && (
        <form onSubmit={saveProfile} className="px-4 py-4 space-y-3 border-b border-[var(--border-color)] dark:border-white/[0.07]">
          <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wide">تعديل البيانات</p>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">الاسم</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="profile-name-input"
                className="w-full border border-[var(--border-color)] dark:border-white/10 dark:bg-[var(--bg-card)]/5 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="اسمك الكامل"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">البريد الإلكتروني</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                data-testid="profile-email-input"
                className="w-full border border-[var(--border-color)] dark:border-white/10 dark:bg-[var(--bg-card)]/5 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="بريدك الإلكتروني"
              />
            </div>
          </div>

          <div className="border-t border-[var(--border-color)] dark:border-white/[0.07] pt-3">
            <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">تغيير كلمة السر (اختياري)</p>
            <div className="space-y-2">
              <input
                type="password"
                value={form.current_password}
                onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                data-testid="profile-current-password"
                className="w-full border border-[var(--border-color)] dark:border-white/10 dark:bg-[var(--bg-card)]/5 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="كلمة السر الحالية"
              />
              <div className="relative">
                <input
                  type={showNewPwd ? "text" : "password"}
                  value={form.new_password}
                  onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                  data-testid="profile-new-password"
                  className="w-full border border-[var(--border-color)] dark:border-white/10 dark:bg-[var(--bg-card)]/5 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 pl-9"
                  placeholder="كلمة السر الجديدة"
                />
                <button type="button" onClick={() => setShowNewPwd(v => !v)}
                  className="absolute left-2.5 top-2.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
                  {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-profile-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#004EEB] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            حفظ التغييرات
          </button>
        </form>
      )}

      {/* ── Logout ── */}
      <div className="p-2">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
            text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10
            transition-colors text-right"
          data-testid="topbar-logout-btn"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          تسجيل الخروج
        </button>
      </div>
    </div>
  );
}

/* ══════════════ TopBar ══════════════ */
export default function TopBar() {
  const { user }                   = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { lang, toggle: toggleLang }   = useLang();
  const navigate                       = useNavigate();

  const [userOpen, setUserOpen]   = useState(false);
  const [helpOpen, setHelpOpen]   = useState(false);
  const [sallaHealth, setSallaHealth] = useState(null);
  const [refreshingToken, setRefreshingToken] = useState(false);

  const userRef = useRef(null);
  const helpRef = useRef(null);

  useOutsideClick(userRef, () => setUserOpen(false));
  useOutsideClick(helpRef, () => setHelpOpen(false));

  /* Poll Salla connection health every 60s */
  useEffect(() => {
    let alive = true;
    const fetchHealth = async () => {
      try {
        const { data } = await api.get("/salla/health");
        if (alive) setSallaHealth(data);
      } catch {}
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const handleManualRefresh = async () => {
    setRefreshingToken(true);
    try {
      await api.post("/salla/refresh-token");
      const { data } = await api.get("/salla/health");
      setSallaHealth(data);
      toast.success("تم تجديد الاتصال بسلة");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر التجديد — قد يلزم إعادة الربط");
    } finally {
      setRefreshingToken(false);
    }
  };

  const initials = (user?.name || "م").slice(0, 1);
  const isDark   = theme === "dark";

  /* ── Render Salla status badge ── */
  const renderSallaBadge = () => {
    if (!sallaHealth || sallaHealth.status === "never_connected") return null;

    const palettes = {
      green: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
      amber: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
      red:   "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
      slate: "bg-[var(--bg-card-hover)] text-[var(--text-secondary)] border-[var(--border-color)] hover:bg-slate-100",
    };
    const cls = palettes[sallaHealth.color] || palettes.slate;
    const icon = sallaHealth.status === "healthy"
      ? <CheckCircle2 className="w-3.5 h-3.5" />
      : sallaHealth.needs_action
        ? <AlertTriangle className="w-3.5 h-3.5" />
        : <Link2 className="w-3.5 h-3.5" />;

    return (
      <div className="relative group">
        <button
          onClick={sallaHealth.needs_action ? handleManualRefresh : () => navigate("/dashboard?settings=1")}
          disabled={refreshingToken}
          data-testid="topbar-salla-badge"
          className={`flex items-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-semibold transition-all disabled:opacity-50 ${cls}`}
          title={sallaHealth.label}
        >
          {refreshingToken
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : icon}
          <span className="hidden lg:inline">
            {sallaHealth.needs_action ? "أعِد ربط Salla" : "Salla"}
          </span>
          {sallaHealth.status === "expiring_soon" && (
            <span className="hidden lg:inline opacity-60">·</span>
          )}
        </button>

        {/* Tooltip */}
        <div className="absolute top-full end-0 mt-1.5 w-56 z-50 hidden group-hover:block">
          <div className="bg-slate-900 text-white text-[11px] rounded-lg shadow-lg p-2.5 space-y-1">
            <p className="font-semibold">{sallaHealth.label}</p>
            {sallaHealth.store_name && (
              <p className="opacity-70">المتجر: {sallaHealth.store_name}</p>
            )}
            {sallaHealth.expires_at && sallaHealth.minutes_remaining > 0 && (
              <p className="opacity-70">
                ينتهي بعد: {sallaHealth.minutes_remaining > 1440
                  ? `${Math.round(sallaHealth.minutes_remaining / 1440)} يوم`
                  : sallaHealth.minutes_remaining > 60
                    ? `${Math.round(sallaHealth.minutes_remaining / 60)} ساعة`
                    : `${sallaHealth.minutes_remaining} دقيقة`}
              </p>
            )}
            {sallaHealth.needs_action && (
              <p className="text-amber-300 mt-1">انقر لمحاولة تجديد الاتصال</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      data-testid="topbar"
      className="hidden md:flex items-center justify-end gap-1
        h-14 px-5 shrink-0
        bg-[var(--bg-card)]/95 dark:bg-[#0c1526]/95 backdrop-blur-md
        border-b border-[var(--border-color)] dark:border-white/[0.06]
        sticky top-0 z-20"
    >

      {/* ─── Salla connection badge ─── */}
      {renderSallaBadge()}

      {sallaHealth && sallaHealth.status !== "never_connected" && (
        <div className="w-px h-5 bg-slate-200 dark:bg-[var(--bg-card)]/[0.08] mx-1" />
      )}

      {/* ─── Help ─── */}
      <div ref={helpRef} className="relative">
        <button
          onClick={() => { setHelpOpen(!helpOpen); setUserOpen(false); }}
          data-testid="topbar-help-btn"
          className="flex items-center gap-1.5 h-9 px-3 rounded-xl
            text-[var(--text-secondary)] dark:text-white/50 hover:text-[var(--text-primary)] dark:hover:text-white
            hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/[0.06] transition-all text-sm font-medium"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="text-[12px]">المساعدة</span>
        </button>
        {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
      </div>

      <div className="w-px h-5 bg-slate-200 dark:bg-[var(--bg-card)]/[0.08] mx-1" />

      {/* ─── Theme toggle ─── */}
      <button
        onClick={toggleTheme}
        data-testid="topbar-theme-btn"
        title={isDark ? "الوضع الفاتح" : "الوضع الداكن"}
        className="w-9 h-9 rounded-xl flex items-center justify-center
          text-[var(--text-secondary)] dark:text-white/50 hover:text-[var(--text-primary)] dark:hover:text-white
          hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/[0.06] transition-all"
      >
        {isDark
          ? <Sun className="w-4 h-4 text-amber-400" />
          : <Moon className="w-4 h-4" />
        }
      </button>

      {/* ─── Language toggle ─── */}
      <button
        onClick={toggleLang}
        data-testid="topbar-lang-btn"
        title={lang === "ar" ? "Switch to English" : "التبديل للعربية"}
        className="w-9 h-9 rounded-xl flex items-center justify-center
          text-[var(--text-secondary)] dark:text-white/50 hover:text-[var(--text-primary)] dark:hover:text-white
          hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/[0.06] transition-all"
      >
        <Globe className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-slate-200 dark:bg-[var(--bg-card)]/[0.08] mx-1" />

      {/* ─── User ─── */}
      <div ref={userRef} className="relative">
        <button
          onClick={() => { setUserOpen(!userOpen); setHelpOpen(false); }}
          data-testid="topbar-user-btn"
          className="flex items-center gap-2 h-9 px-2 rounded-xl
            hover:bg-slate-100 dark:hover:bg-[var(--bg-card)]/[0.06] transition-all"
        >
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#004EEB] to-[#0040cc]
            grid place-items-center text-white text-[11px] font-bold shrink-0">
            {initials}
          </div>
          <span className="text-[12px] font-semibold text-[var(--text-secondary)] dark:text-white/80
            max-w-[100px] truncate hidden lg:block">
            {user?.name}
          </span>
          <ChevronDown className={`w-3 h-3 text-[var(--text-muted)] dark:text-white/30 transition-transform ${userOpen ? "rotate-180" : ""}`} />
        </button>
        {userOpen && <UserPanel onClose={() => setUserOpen(false)} />}
      </div>

    </header>
  );
}
